import * as React from 'react';
import { useMemo } from 'react';
import { css } from '@emotion/core';
import { Link, useHistory } from 'react-router-dom';
import BulletedMsg from '../../layout/BulletedMsg';
import { faCheckCircle, faHistory } from '@fortawesome/free-solid-svg-icons';
import { Str } from '../../localization/translationKeys';
import { Routes } from '../../navigation/routes';
import { COLORS } from '../../globalStyle/colors';
import { faCircle as faCircleHollow } from '@fortawesome/free-regular-svg-icons/faCircle';
import { faCircle as faCircleSolid } from '@fortawesome/free-solid-svg-icons/faCircle';
import { ParticipantProject } from '../models';
import { useAppState } from '../../core/redux/hooks';
import { pathCombine } from '../../core/api/module';
import { BaseUrl } from '../../core/api/endpoints';
import Msg from '../../localization/Msg';


const styles = {
  root:           css`
    border: 1px solid ${COLORS.LIGHTER_GREY};
    border-radius: 8px;
    
    & > * {
      border-bottom: 1px solid ${COLORS.LIGHTER_GREY};
    }
    
    & > *:last-child {
      border-bottom: none;
    }
  `,
  link:           css`
    display: block;
    padding: 18px 15px;
    text-decoration: none;
    
    &:hover {
      cursor: pointer;
    }
  `,
  stepLink:       css`
    color: ${COLORS.MEDIUM_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  activeLink:     css`
    color: ${COLORS.DARK_GREY};
    background-color: ${COLORS.LIGHTER_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  disabledLink:   css`
    opacity: .4;
    border-bottom: 1px solid ${COLORS.LIGHT_GREY};
  `,
  iconBlue:       css`
    font-size: 15px;
    color: ${COLORS.BLUE};
  `,
  iconGreen:      css`
    font-size: 15px;
    color: ${COLORS.GREEN};
  `,
  iconMediumGrey: css`
    font-size: 15px;
    color: ${COLORS.MEDIUM_GREY};
  `,
  iconDarkGrey:   css`
    font-size: 15px;
    color: ${COLORS.DARK_GREY};
  `,
  title:         css`
    display: block;
    font-size: 11px;
    color: ${COLORS.DARK_GREY};
    margin-bottom: 8px;
    text-transform: uppercase;
  `,
};


const activeLink = (route: string) => (
  window.location.pathname.endsWith(route)
);

const iconFor = (
  nextStep: boolean,
  started: boolean,
  complete: boolean,
) => (
  complete
    ? faCheckCircle
    : nextStep
    ? faCircleSolid
    : started
      ? faHistory
      : faCircleHollow
);

const iconColorFor = (
  nextStep: boolean,
  started: boolean,
  complete: boolean,
  active: boolean,
) => (
  complete
    ? styles.iconBlue
    : nextStep || started
    ? styles.iconGreen
    : active
      ? styles.iconDarkGrey
      : styles.iconMediumGrey
);

type Props = {
  routes: Routes;
  proj: ParticipantProject | undefined;
  participantId: number;
  className?: string;
};


/**
 * ProjectSteps: a component that displays menu items
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectSteps: React.FC<Props> = props => {
  const project = props.proj;
  const routes  = props.routes;

  const requirements    = useAppState(state => state.participantRequirements);
  const requirementsMet = useMemo(
    () => (requirements || []).length > 0 && requirements?.every(s => s.invitedCount >= s.minimumRequired),
    [requirements],
  );

  /* memoize all the project settings */
  const followupSurveyStep          = useMemo(() => project?.followupSurveyStep, [project]);
  const followupSurveyStarted       = useMemo(() => project?.followupSurveyStarted, [project]);
  const followupSurveyCreated       = useMemo(() => project?.followupSurveyCreated, [project]);
  const inviteRatersStep            = useMemo(() => project?.inviteRatersStep, [project]);
  const approval                    = useMemo(() => project?.inviteRatersRaterApprovalOption, [project]);
  const raterInvited                = useMemo(() => project?.raterInvited, [project]);
  const sentForApproval             = useMemo(() => project?.approverEmailSent, [project]);
  const selfSurveyStep              = useMemo(() => project?.selfSurveyStep, [project]);
  const selfSurveyInProgress        = useMemo(() => (project?.selfSurveyAmountComplete ?? 0) > 0, [project]);
  const selfSurveyCompleted         = useMemo(() => project?.selfSurveyCompleted, [project]);
  const setGoalsStep                = useMemo(() => project?.setGoalsStep, [project]);
  const setGoalsAmountComplete      = useMemo(() => project?.setGoalsAmountComplete, [project]);
  const viewReportStep              = useMemo(() => project?.viewReportStep, [project]);
  const reportAvailable             = useMemo(() => project?.reportAvailable, [project]);
  const devPlanInteractive          = useMemo(() => project?.developmentPlanUseInteractive, [project]);
  const devPlanStaticUrl            = useMemo(() => project?.developmentPlanPageUrl, [project]);
  const developmentPlanStep         = useMemo(() => project?.developmentPlanStep, [project]);
  const devPlanAmountComplete       = useMemo(() => project?.devPlanAmountComplete, [project]);
  const developmentPlanEmailLevelId = useMemo(() => project?.developmentPlanEmailLevelId, [project]);
  const developmentPlanGoalsToTrack = useMemo(() => project?.developmentPlanGoalsToTrack, [project]);
  const devPlanEmailSent            = useMemo(() => project?.devPlanEmailSent, [project]);
  const devPlanItemCount            = useMemo(() => project?.devPlanItemCount, [project]);
  const devPlanViewed               = useAppState(state => state.devPlan?.viewed ?? false);
  const pastDue                     = useMemo(() => project?.surveyPastDue, [project]);
  const passkeyExpired              = useMemo(() => project?.passkeyExpired, [project]);

  const history = useHistory();

  const lowerCasePath = history.location.pathname.toLowerCase();
  const viewReportActive = lowerCasePath.indexOf(routes.FeedbackReport.toLowerCase()) > -1;

  const inviteStarted = useMemo(
    () => approval ? sentForApproval : raterInvited,
    [approval, sentForApproval, raterInvited],
  );

  /* Create Survey */
  const createSurveyNextStep = useMemo(
    () => followupSurveyStep && !followupSurveyStarted,
    [followupSurveyStep, followupSurveyStarted],
  );
  /* Invite Raters */
  const inviteRatersNextStep = useMemo(
    () => !createSurveyNextStep
          && inviteRatersStep && !inviteStarted,
    [createSurveyNextStep, inviteRatersStep, inviteStarted],
  );
  const inviteRatersDisabled = useMemo(
    () => followupSurveyStep && !followupSurveyCreated,
    [followupSurveyStep, followupSurveyCreated],
  );
  const inviteRatersComplete = useMemo(
    () => inviteStarted && requirementsMet,
    [inviteStarted, requirementsMet],
  );
  /* Self Survey */
  const selfSurveyNextStep = useMemo(
    () => !createSurveyNextStep && !inviteRatersNextStep
          && selfSurveyStep && !selfSurveyInProgress,
    [
      createSurveyNextStep,
      inviteRatersNextStep,
      selfSurveyStep,
      selfSurveyInProgress,
    ],
  );
  const selfSurveyDisabled = useMemo(
    () => passkeyExpired
          || (followupSurveyStep && !followupSurveyCreated)
          || selfSurveyCompleted!,
    [
      passkeyExpired,
      followupSurveyStep,
      followupSurveyCreated,
      selfSurveyCompleted,
    ],
  );
  /* Set Goals */
  const setGoalsNextStep = useMemo(
    () => !createSurveyNextStep && !inviteRatersNextStep && !selfSurveyNextStep
          && setGoalsStep && (setGoalsAmountComplete || 0) === 0,
    [
      createSurveyNextStep,
      inviteRatersNextStep,
      selfSurveyNextStep,
      setGoalsStep,
      setGoalsAmountComplete,
    ],
  );
  /* DevPlan */
  const noDevPlanRequirements = useMemo(
    () => developmentPlanEmailLevelId === 0
          && developmentPlanGoalsToTrack === 0,
    [developmentPlanEmailLevelId, developmentPlanGoalsToTrack],
  );
  const devPlanStarted        = useMemo(
    () => followupSurveyStep
      ? devPlanViewed
      : noDevPlanRequirements
        ? devPlanEmailSent || devPlanItemCount! > 0
        : devPlanAmountComplete! > 0,
    [
      noDevPlanRequirements,
      devPlanEmailSent,
      devPlanItemCount,
      devPlanAmountComplete,
      devPlanViewed,
      followupSurveyStep,
    ],
  );
  const devPlanNextStep       = useMemo(
    () => !createSurveyNextStep && !inviteRatersNextStep && !selfSurveyNextStep
            && !setGoalsNextStep && reportAvailable
            && developmentPlanStep && !devPlanStarted,
    [
      createSurveyNextStep,
      inviteRatersNextStep,
      selfSurveyNextStep,
      setGoalsNextStep,
      reportAvailable,
      developmentPlanStep,
      devPlanStarted,
    ],
  );
  const devPlanComplete       = useMemo(
    () => followupSurveyStep
          ? devPlanViewed
          : noDevPlanRequirements && devPlanAmountComplete === 1 && devPlanStarted
    ,
    [noDevPlanRequirements, devPlanAmountComplete, devPlanStarted, followupSurveyStep, devPlanViewed],
  );

  const staticDevPlanUrl     = useMemo(
    () => {
      const staticUrl = devPlanStaticUrl || '';
      if (staticUrl === '') {
        return '#';
      }
      if (staticUrl.toLowerCase().startsWith('http')) {
        return staticUrl
      }
      const baseUrl = BaseUrl.substring(0, BaseUrl.length - 4); // ends with '/api'
      const lcUrl = staticUrl.toLowerCase();

      return lcUrl.startsWith('developmentplans/') || lcUrl.startsWith('resources/')
        ? pathCombine(baseUrl, staticUrl)
        : pathCombine(baseUrl, 'DevelopmentPlans/', staticUrl)
    },
    [devPlanStaticUrl],
  );

  /* Hide the whole Assessment group when the project has no portal steps selected, e.g. an e-book-only product
     that serves nothing but custom resources (TX-1378). Kept after every hook call so hook order stays stable. */
  const hasSteps = useMemo(
    () => !!(
      followupSurveyStep
      || inviteRatersStep
      || selfSurveyStep
      || setGoalsStep
      || viewReportStep
      || developmentPlanStep
    ),
    [
      followupSurveyStep,
      inviteRatersStep,
      selfSurveyStep,
      setGoalsStep,
      viewReportStep,
      developmentPlanStep,
    ],
  );

  if (!hasSteps) {
    return null;
  }

  return (
    <React.Fragment>
      <Msg str={Str.Assessment} css={styles.title} defaultMsg={'Assessment'} />
      <div css={styles.root} className={props.className}>
        {
          (followupSurveyStep) && (
            <Link
              to={pastDue || !project?.courseAllowCreateSurvey ? '#' : routes.CreateSurvey}
              css={[
                styles.link,
                activeLink(routes.CreateSurvey) ? styles.activeLink : styles.stepLink,
                (pastDue || !project?.courseAllowCreateSurvey) && styles.disabledLink,
              ]}
            >
              <BulletedMsg
                icon={
                  iconFor(
                    createSurveyNextStep!,
                    followupSurveyStarted!,
                    followupSurveyCreated!,
                  )
                }
                msg={Str.CreateASurvey}
                iconStyle={
                  iconColorFor(
                    createSurveyNextStep!,
                    followupSurveyStarted!,
                    followupSurveyCreated!,
                    activeLink(routes.CreateSurvey),
                  )
                }
                spacePx={14}
              />
            </Link>
          )
        } {
        (inviteRatersStep) && (
          <Link
            to={inviteRatersDisabled || !project?.courseAllowInviteRaters ? '#' : routes.InviteRaters}
            css={[
              styles.link,
              activeLink(routes.InviteRaters) ? styles.activeLink : styles.stepLink,
              (inviteRatersDisabled || !project?.courseAllowInviteRaters) && styles.disabledLink,
            ]}
          >
            <BulletedMsg
              icon={
                iconFor(
                  inviteRatersNextStep!,
                  inviteStarted!,
                  inviteRatersComplete!,
                )
              }
              msg={Str.InviteRemindRaters}
              iconStyle={
                iconColorFor(
                  inviteRatersNextStep!,
                  inviteStarted!,
                  inviteRatersComplete!,
                  activeLink(routes.InviteRaters),
                )
              }
              spacePx={14}
            />
          </Link>
        )
      }
        {
          (selfSurveyStep) && (
            <Link
              to={selfSurveyDisabled || !project?.courseAllowSelfSurvey ? '#' : routes.SelfSurvey}
              css={[
                styles.link,
                activeLink(routes.SelfSurvey) ? styles.activeLink : styles.stepLink,
                (selfSurveyDisabled || !project?.courseAllowSelfSurvey) && styles.disabledLink,
              ]}
            >
              <BulletedMsg
                icon={
                  iconFor(
                    selfSurveyNextStep!,
                    selfSurveyInProgress!,
                    selfSurveyCompleted!,
                  )
                }
                msg={Str.TakeSelfSurvey}
                iconStyle={
                  iconColorFor(
                    selfSurveyNextStep!,
                    selfSurveyInProgress!,
                    selfSurveyCompleted!,
                    activeLink(routes.SelfSurvey),
                  )
                }
                spacePx={14}
              />
            </Link>
          )
        }
        {
          (setGoalsStep) && (
            <Link
              to={!project?.courseAllowSetGoals ? '#' : routes.SetGoals}
              css={[
                styles.link,
                activeLink(routes.SetGoals) ? styles.activeLink : styles.stepLink,
                !project?.courseAllowSetGoals && styles.disabledLink,
              ]}
            >
              <BulletedMsg
                icon={
                  iconFor(
                    setGoalsNextStep!,
                    setGoalsAmountComplete! > 0,
                    setGoalsAmountComplete! === 1,
                  )
                }
                msg={Str.SetGoals}
                iconStyle={
                  iconColorFor(
                    setGoalsNextStep!,
                    setGoalsAmountComplete! > 0,
                    setGoalsAmountComplete! === 1,
                    activeLink(routes.SetGoals),
                  )
                }
                spacePx={14}
              />
            </Link>
          )
        }

        {
          (viewReportStep) && (
            <Link
              to={reportAvailable ? routes.FeedbackReport : '#'}
              onClick={() => viewReportActive && window.location.reload()}
              css={[
                styles.link,
                activeLink(routes.FeedbackReport) ? styles.activeLink : styles.stepLink,
                !reportAvailable && styles.disabledLink,
              ]}
            >
              <BulletedMsg
                icon={reportAvailable ? faCheckCircle : faCircleHollow}
                msg={Str.ViewReport}
                iconStyle={reportAvailable ? styles.iconBlue : styles.iconMediumGrey}
                spacePx={14}
              />
            </Link>
          )
        }
        {
          (developmentPlanStep) && (
            devPlanInteractive 
            ? <Link
                to={!project?.courseAllowDevPlan ? '#' : routes.DevPlan}
                css={[
                  styles.link,
                  activeLink(routes.DevPlan) ? styles.activeLink : styles.stepLink,
                  !project?.courseAllowDevPlan && styles.disabledLink,
                ]}
              >
                <BulletedMsg
                  icon={
                    iconFor(
                      devPlanNextStep!,
                      devPlanStarted,
                      devPlanComplete,
                    )
                  }
                  msg={
                    followupSurveyStep
                    ? Str.ReviewDevPlan
                      : devPlanInteractive
                        ? Str.CreateDevPlan
                        : Str.DevPlan
                  }
                  iconStyle={
                    iconColorFor(
                      devPlanNextStep!,
                      devPlanStarted,
                      devPlanComplete,
                      activeLink(routes.DevPlan),
                    )
                  }
                  spacePx={14}
                />
              </Link>
            : <a
                href={staticDevPlanUrl}
                css={[
                  styles.link,
                  activeLink(routes.DevPlan) ? styles.activeLink : styles.stepLink,
                ]}
                target={'_blank'}
                rel="noopener noreferrer"
              >
                <BulletedMsg
                  icon={
                    iconFor(
                      devPlanNextStep!,
                      devPlanStarted,
                      devPlanComplete,
                    )
                  }
                  msg={
                    followupSurveyStep
                    ? Str.ReviewDevPlan
                      : devPlanInteractive
                        ? Str.CreateDevPlan
                        : Str.DevPlan
                  }
                  iconStyle={
                    iconColorFor(
                      devPlanNextStep!,
                      devPlanStarted,
                      devPlanComplete,
                      activeLink(routes.DevPlan),
                    )
                  }
                  spacePx={14}
                />
              </a>
          )
        }
      </div>
    </React.Fragment>
  );
};

export default ProjectSteps;
